import { useEffect, useRef } from 'react';
import { useSimulationContext } from '../providers/SimulationProvider';
import { toDataUrl, isIOS } from '../helpers/utils';
import { getPersistentAudio } from '../helpers/persistentAudio';
import useAudioMessageListener from './useAudioMessageListener';

/**
 * `onItemPlay` (optional) is called with the queued item each time one starts
 * playing — extra fields passed to enqueueAudio, such as a speaker's bot id,
 * come back with it.
 */
function useAudioQueue({ onItemPlay } = {}) {
  const audioRef = useRef(null);
  // Playback handlers are created once per chunk, so they read the latest
  // callback through a ref rather than the one from the render that queued it.
  const onItemPlayRef = useRef(onItemPlay);
  onItemPlayRef.current = onItemPlay;
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const persistentAudioRef = useRef(getPersistentAudio());
  /**
   * Bumped by every clearAudioQueue(). Each chunk records the epoch it was
   * queued under, so the callbacks it scheduled — canplaythrough, ended, the
   * play() rejection — can tell whether they still belong to the current
   * reply. Element identity cannot answer that: every chunk plays through the
   * same persistent element, so `audioRef.current !== audio` is never true.
   */
  const epochRef = useRef(0);

  const eventHandlersRef = useRef(null);

  const { setIsAiTalking, setFacialData, setIsLocalAudioPlaying } =
    useSimulationContext();

  /**
   * Playback state is kept both as a ref, read synchronously while queueing,
   * and in context, where AnamVideoStream re-renders on it: showing the avatar
   * again mid-reply has to wait for locally played audio to finish.
   */
  const setPlaybackActive = (isActive) => {
    isPlayingRef.current = isActive;
    setIsLocalAudioPlaying(isActive);
  };

  // Use the mute hook for audio mute/unmute functionality
  const { mutedRef } = useAudioMessageListener({ audioRef });

  const removeAudioEventListeners = (audio) => {
    if (!audio || !audio._handlers) return;

    const {
      handlePlay,
      handleEnd,
      handleError,
      handleCanPlayThrough,
      handleLoadedData
    } = audio._handlers;

    // Detach from the element that was passed in, not from audioRef — a clear
    // nulls audioRef before its listeners are gone.
    audio.removeEventListener('play', handlePlay);
    audio.removeEventListener('ended', handleEnd);
    audio.removeEventListener('error', handleError);
    audio.removeEventListener('canplaythrough', handleCanPlayThrough);
    if (handleLoadedData) {
      audio.removeEventListener('loadeddata', handleLoadedData);
    }

    eventHandlersRef.current = null;
    audio._handlers = null;
  };

  /**
   * Stops and cleans up current audio element completely
   */
  const stopAndCleanupAudio = (audio) => {
    if (!audio) return;
    // Remove event listeners first
    removeAudioEventListeners(audio);

    // Then stop and clean up audio
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      audio.load();
    } catch (error) {
      console.error('[Audio Queue Error] Error stopping audio:', error);
    }

    audioRef.current = null;
  };

  const resetAnimationState = () => {
    setIsAiTalking(false);
    setFacialData([]);
  };

  /**
   * Plays the next audio in the queue
   * Automatically handles queue progression and error cases
   */
  const playNextInQueue = () => {
    // If queue is empty, stop playing
    if (audioQueueRef.current.length === 0) {
      setPlaybackActive(false);
      stopAndCleanupAudio(audioRef.current);
      resetAnimationState();
      return;
    }

    // Clean up previous audio before starting new one
    const isContinuingPlayback = isPlayingRef.current;
    stopAndCleanupAudio(audioRef.current);

    // Stay in "talking" across queued chunks; only reset when starting fresh.
    if (!isContinuingPlayback) {
      resetAnimationState();
    } else {
      setFacialData([]);
    }

    // Mark as currently playing
    setPlaybackActive(true);

    // Get the first audio from queue (but don't remove yet)
    // MUST be inside this function to get the correct current item!
    const currentItem = audioQueueRef.current[0];
    const voiceData = currentItem.audio;
    const itemEpoch = currentItem.epoch;

    try {
      // Create new audio element
      // const audio = new Audio();
      const audio = persistentAudioRef.current;
      audio.preload = 'auto';
      audioRef.current = audio;

      // Define event handlers - MUST be here to capture correct currentItem
      const handleAudioPlay = () => {
        // Audio is playing regardless of whether lip-sync data came with it
        // (Anam-rendered simulations send audio without visemes).
        setIsAiTalking(true);
        onItemPlayRef.current?.(currentItem);

        // Set facial data (viseme) for the current audio
        if (currentItem.viseme && currentItem.viseme.length > 0) {
          setFacialData(currentItem.viseme);
        }
      };

      const handleAudioEnd = () => {
        // Clean up listeners first
        removeAudioEventListeners(audio);

        // A clear already emptied the queue and may have queued a fresh reply;
        // shifting here would drop its first chunk.
        if (itemEpoch !== epochRef.current) return;

        // Remove the played audio from queue
        setFacialData([]);
        audioQueueRef.current.shift();

        const hasMoreInQueue = audioQueueRef.current.length > 0;
        if (!hasMoreInQueue) {
          setIsAiTalking(false);
        }

        // Play next audio in queue
        playNextInQueue();
      };

      const handleAudioError = (error) => {
        console.error('[Audio Queue Error] Audio playback error:', error);

        // Clean up listeners first
        removeAudioEventListeners(audio);

        if (itemEpoch !== epochRef.current) return;

        // Remove the failed audio from queue and try next
        audioQueueRef.current.shift();
        playNextInQueue();
      };

      let playbackStarted = false;

      const startPlayback = () => {
        if (playbackStarted) return;
        if (audioRef.current !== audio) return;

        // Check if audio is still valid (not cleared). Comparing elements is
        // useless here — every chunk plays through the same persistent element,
        // so the identity always matches. The epoch is what actually changes
        // when the queue is cleared.
        if (itemEpoch !== epochRef.current) {
          removeAudioEventListeners(audio);
          return;
        }

        playbackStarted = true;
        audio.currentTime = 0;

        audio
          .play()
          .then()
          .catch((playError) => {
            console.error('[Audio Queue Error] Audio play failed:', playError);
            removeAudioEventListeners(audio);

            if (playError?.name === 'NotAllowedError' && isIOS()) {
              setPlaybackActive(false);
              return;
            }

            if (itemEpoch !== epochRef.current) return;

            audioQueueRef.current.shift();
            playNextInQueue();
          });
      };

      const handleAudioCanplaythrough = () => {
        startPlayback();
      };

      const handleAudioLoadedData = () => {
        if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          startPlayback();
        }
      };

      // Store handlers so we can remove them later
      audio._handlers = {
        handlePlay: handleAudioPlay,
        handleEnd: handleAudioEnd,
        handleError: handleAudioError,
        handleCanPlayThrough: handleAudioCanplaythrough,
        handleLoadedData: handleAudioLoadedData
      };

      // Add event listeners
      audioRef.current.addEventListener('play', handleAudioPlay);
      audioRef.current.addEventListener('ended', handleAudioEnd);
      audioRef.current.addEventListener('error', handleAudioError);
      audioRef.current.addEventListener(
        'canplaythrough',
        handleAudioCanplaythrough,
        { once: true }
      );
      audioRef.current.addEventListener('loadeddata', handleAudioLoadedData, {
        once: true
      });

      // Set the source and load (convert base64 to data URL)
      audioRef.current.src = toDataUrl(voiceData);
      audioRef.current.load();
      audioRef.current.muted = mutedRef.current;

      if (audioRef.current.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        startPlayback();
      }
    } catch (error) {
      console.error('[Audio Queue Error] Error creating audio element:', error);

      // Remove the failed audio from queue and try next
      audioQueueRef.current.shift();
      playNextInQueue();
    }
  };

  const enqueueAudio = (audioData) => {
    audioQueueRef.current.push({ ...audioData, epoch: epochRef.current });

    // If not currently playing, start playing the queue
    if (!isPlayingRef.current) {
      playNextInQueue();
    }
  };

  // Clear audio queue (useful for interruptions)
  const clearAudioQueue = () => {
    /**
     * Whether this queue is the one actually making sound. On the Anam route
     * the coach speaks through the avatar's stream and nothing is queued here,
     * so clearing must stay silent: reporting "the voice stopped" while Anam is
     * still speaking hides the Stop button mid-reply, and nothing re-asserts
     * isAiTalking until the analyser next changes its mind.
     */
    const ownsPlayback =
      isPlayingRef.current ||
      audioQueueRef.current.length > 0 ||
      !!audioRef.current;

    // Invalidate everything queued or scheduled so far — chunks that are
    // already mid-load resolve into a stale epoch and are dropped.
    epochRef.current += 1;

    // Clear the queue first
    audioQueueRef.current = [];

    // Stop and cleanup audio (removes listeners and stops playback)
    stopAndCleanupAudio(audioRef.current);

    if (!ownsPlayback) return;

    // Reset flags
    setPlaybackActive(false);

    // Reset context state
    resetAnimationState();
  };

  // Cleanup on unmount: stop all audio playback and clear queue
  useEffect(() => {
    return () => clearAudioQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isPlayingRef,
    audioRef,
    mutedRef,
    enqueueAudio,
    clearAudioQueue
  };
}

export default useAudioQueue;
